import { toPlayableCollection, validateCollection, type CardCollection } from "../shared/collections";
import type { PublicRoom } from "../shared/protocol";

export const ROOM_COLLECTION_ERRORS = {
  uploadNetwork: "Impossible d’envoyer ce jeu. Vérifie ta connexion et réessaie.",
  downloadNetwork: "Impossible de charger les images du salon. Vérifie ta connexion et réessaie.",
  uploadTimeout: "L’envoi du jeu prend trop de temps. Réessaie.",
  downloadTimeout: "Le chargement du jeu prend trop de temps. Réessaie.",
  uploadResponse: "Le serveur n’a pas confirmé l’envoi du jeu. Réessaie.",
  downloadResponse: "Le serveur n’a pas pu charger ce jeu. Réessaie.",
  invalid: "Les images de ce salon sont invalides.",
  mismatch: "Les images reçues ne correspondent pas à ce salon.",
} as const;

const CACHE_LIMIT = 8;
const REQUEST_TIMEOUT_MS = 20_000;
const collections = new Map<string, Promise<CardCollection>>();
const serverCollectionId = /^[A-Za-z0-9_-]{32}$/;

function isTimeout(error: unknown, signal: AbortSignal): boolean {
  return signal.aborted || (error instanceof Error && ["AbortError", "TimeoutError"].includes(error.name));
}

function remember(id: string, collection: Promise<CardCollection>): void {
  collections.delete(id);
  collections.set(id, collection);
  while (collections.size > CACHE_LIMIT) collections.delete(collections.keys().next().value!);
}

async function requestJson(url: string, options: RequestInit, upload: boolean): Promise<unknown> {
  const signal = AbortSignal.timeout(REQUEST_TIMEOUT_MS);
  let response: Response;
  try {
    response = await fetch(url, { ...options, signal });
  } catch (error) {
    throw new Error(isTimeout(error, signal)
      ? upload ? ROOM_COLLECTION_ERRORS.uploadTimeout : ROOM_COLLECTION_ERRORS.downloadTimeout
      : upload ? ROOM_COLLECTION_ERRORS.uploadNetwork : ROOM_COLLECTION_ERRORS.downloadNetwork);
  }
  let payload: unknown;
  try { payload = await response.json(); } catch (error) {
    throw new Error(isTimeout(error, signal)
      ? upload ? ROOM_COLLECTION_ERRORS.uploadTimeout : ROOM_COLLECTION_ERRORS.downloadTimeout
      : upload ? ROOM_COLLECTION_ERRORS.uploadResponse : ROOM_COLLECTION_ERRORS.downloadResponse);
  }
  if (!response.ok) {
    const message = payload && typeof payload === "object" && "error" in payload && typeof payload.error === "string"
      ? payload.error : upload ? ROOM_COLLECTION_ERRORS.uploadResponse : ROOM_COLLECTION_ERRORS.downloadResponse;
    throw new Error(message);
  }
  return payload;
}

/** Upload cropped raster assets once. The editable originals never leave this browser. */
export async function uploadRoomCollection(collection: CardCollection): Promise<string> {
  const playable = toPlayableCollection(collection);
  const payload = await requestJson("/api/collections", {
    method: "POST",
    headers: { "content-type": "application/json" },
    body: JSON.stringify(playable),
    cache: "no-store",
  }, true);
  if (!payload || typeof payload !== "object" || !("id" in payload)
    || typeof payload.id !== "string" || !serverCollectionId.test(payload.id)) {
    throw new Error(ROOM_COLLECTION_ERRORS.uploadResponse);
  }
  remember(payload.id, Promise.resolve(playable));
  return payload.id;
}

function matchesRoom(collection: CardCollection, room: PublicRoom): void {
  if (collection.order !== room.order || collection.name !== room.collection?.name) {
    throw new Error(ROOM_COLLECTION_ERRORS.mismatch);
  }
}

/** A joined room's images live in memory; saving them to the user's library is explicit. */
export async function loadRoomCollection(room: PublicRoom): Promise<CardCollection | null> {
  if (!room.collection) return null;
  const { id, name } = room.collection;
  if (typeof id !== "string" || !serverCollectionId.test(id) || typeof name !== "string" || !name.trim() || name.length > 60) {
    throw new Error(ROOM_COLLECTION_ERRORS.invalid);
  }
  let pending = collections.get(id);
  if (!pending) {
    pending = requestJson(`/api/rooms/${encodeURIComponent(room.code)}/collection`, { cache: "no-store" }, false)
      .then(payload => {
        const result = validateCollection(payload, { playable: true, includeSources: false });
        if (!result.valid || !result.collection) throw new Error(ROOM_COLLECTION_ERRORS.invalid);
        matchesRoom(result.collection, room);
        return result.collection;
      })
      .catch(error => {
        if (collections.get(id) === pending) collections.delete(id);
        throw error;
      });
    remember(id, pending);
  }
  const collection = await pending;
  // Recheck metadata even on cache hits (for example after a reconnect).
  matchesRoom(collection, room);
  return structuredClone(collection);
}
